import { defineStore } from "pinia";
import { useErrorStore } from "./error";
import { ref } from "vue";
import { useCookie, useRuntimeConfig } from "#app";

interface LoginData {
  username: string;
  password: string;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
}

interface ForgotPasswordData {
  email: string;
}

interface ResetPasswordData {
  email: string;
  token: string;
  password: string;
}

export const useUserStore = defineStore("user", () => {
  const chats = ref<ChatListItem[]>([]);
  const isLoggedIn = ref(false);
  const apiKey = ref("");
  const username = ref("");
  const subscription = ref();
  const isCollapsed = ref(false);
  const sidebarWidth = ref(330);

  const errorStore = useErrorStore();
  const config = useRuntimeConfig();
  const baseURL = config.public.baseURL;

  const AUTH_TOKEN_KEY = "auth_token";
  const USERNAME_KEY = "username";
  const SUBSCRIPTION = "subscription";
  const IS_COLLAPSED = "is_collapsed";
  const SIDEBAR_WIDTH = "sidebar_width";

  const authTokenCookie = useCookie(AUTH_TOKEN_KEY);
  const usernameCookie = useCookie(USERNAME_KEY);
  const subscriptionCookie = useCookie(SUBSCRIPTION);
  const isCollapsedCookie = useCookie(IS_COLLAPSED);
  const sidebarWidthCookie = useCookie(SIDEBAR_WIDTH);

  if (authTokenCookie.value) {
    apiKey.value = authTokenCookie.value;
    isLoggedIn.value = true;
  }

  if (usernameCookie.value) {
    username.value = usernameCookie.value;
  }

  if (subscriptionCookie.value) {
    subscription.value = subscriptionCookie.value;
  }

  isCollapsed.value = isCollapsedCookie.value === "true";

  if (sidebarWidthCookie.value) {
    const n = Number(sidebarWidthCookie.value);
    if (!Number.isNaN(n)) sidebarWidth.value = n;
  }

  const setSidebarCookie = (width: number) => {
    sidebarWidthCookie.value = String(width);
  };

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
    isCollapsedCookie.value = isCollapsed.value ? "true" : "false";
  };

  const login = async (userData: LoginData): Promise<boolean> => {
    try {
      const response = await fetch(`${baseURL}/api/login`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userData.username,
          password: userData.password,
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (response.ok) {
        isLoggedIn.value = true;
        apiKey.value = responseData.token;
        username.value = userData.username;

        subscription.value = responseData.subscription;
        subscriptionCookie.value = subscription.value;

        authTokenCookie.value = apiKey.value;
        usernameCookie.value = username.value;

        return true;
      }

      const msg =
        responseData?.message || responseData?.error || "Login failed.";
      errorStore.setError(msg);
      throw new Error(msg);
    } catch (error: any) {
      const msg = error.message || "An error occurred while trying to log in.";
      if (!errorStore.error) errorStore.setError(msg);
      throw error;
    }
  };

  // Store an auth token obtained via an OAuth redirect (e.g. Google login).
  // Mirrors the success branch of login() so the rest of the app behaves the same.
  const loginWithToken = (data: {
    token: string;
    email?: string;
    subscription?: string;
  }): void => {
    isLoggedIn.value = true;
    apiKey.value = data.token;
    authTokenCookie.value = data.token;

    if (data.email) {
      username.value = data.email;
      usernameCookie.value = data.email;
    }

    subscription.value = data.subscription ? { name: data.subscription } : "";
    subscriptionCookie.value = subscription.value;
  };

  const register = async (userData: RegisterData): Promise<void> => {
    try {
      const response = await fetch(`${baseURL}/api/register`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        let apiErrorMessage = "Registratie mislukt.";
        if (responseData.message) {
          apiErrorMessage = responseData.message;
        } else if (responseData.errors) {
          apiErrorMessage = Object.values(responseData.errors).flat().join(" ");
        }
        throw new Error(apiErrorMessage);
      }
    } catch (networkError: any) {
      const errorMessage =
        networkError.message || "Er is een netwerkfout opgetreden.";
      errorStore.setError(errorMessage);
      throw networkError;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      if (apiKey.value) {
        await fetch(`${baseURL}/api/logout`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey.value}`,
          },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      isLoggedIn.value = false;
      apiKey.value = "";
      username.value = "";
      chats.value = [];
      subscription.value = "";

      authTokenCookie.value = null;
      usernameCookie.value = null;
      subscriptionCookie.value = null;
    }
  };

  const validateUser = async (): Promise<boolean> => {
    if (!apiKey.value) {
      isLoggedIn.value = false;
      return false;
    }

    try {
      const response = await fetch(`${baseURL}/api/user`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey.value}`,
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        username.value = responseData.username || username.value;
        isLoggedIn.value = true;
        return true;
      } else {
        isLoggedIn.value = false;
        apiKey.value = "";
        authTokenCookie.value = null;
        return false;
      }
    } catch (error) {
      isLoggedIn.value = false;
      return false;
    }
  };

  const forgotPassword = async (data: ForgotPasswordData): Promise<void> => {
    try {
      const response = await fetch(`${baseURL}/api/forgot-password`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        let apiErrorMessage = "Wachtwoord reset aanvragen mislukt.";
        if (responseData.message) apiErrorMessage = responseData.message;
        else if (responseData.errors)
          apiErrorMessage = Object.values(responseData.errors).flat().join(" ");
        throw new Error(apiErrorMessage);
      }
    } catch (networkError: any) {
      const errorMessage =
        networkError.message || "Er is een netwerkfout opgetreden.";
      errorStore.setError(errorMessage);
      throw networkError;
    }
  };

  const resetPassword = async (data: ResetPasswordData): Promise<void> => {
    try {
      const response = await fetch(`${baseURL}/api/reset-password`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!response.ok) {
        let apiErrorMessage = "Wachtwoord reset mislukt.";
        if (responseData.message) apiErrorMessage = responseData.message;
        else if (responseData.errors)
          apiErrorMessage = Object.values(responseData.errors).flat().join(" ");
        throw new Error(apiErrorMessage);
      }
    } catch (networkError: any) {
      const errorMessage =
        networkError.message || "Er is een netwerkfout opgetreden.";
      errorStore.setError(errorMessage);
      throw networkError;
    }
  };

  return {
    chats,
    isLoggedIn,
    apiKey,
    username,
    subscription,
    isCollapsed,
    sidebarWidth,
    setSidebarCookie,
    toggleCollapse,
    login,
    loginWithToken,
    register,
    logout,
    validateUser,
    forgotPassword,
    resetPassword,
  };
});
